/**
 * AgentGeneral — Aba "Geral" do Edit Agent Modal
 * Source: openclaw.json (config) + workspace/.env (integrations)
 */
import { useState, useEffect, useRef } from 'preact/hooks';
import { isAdmin } from '../stores/auth.store';
import { isDockerMode } from '../lib/openclawPaths';
import { LockOverlay } from './LockOverlay';
import { api } from '../api/client';
import { SkeletonBar } from './ui';
import { T } from '../styles/tokens';
import { Button, Textarea } from './ui';

interface FieldSync {
  source: 'ssh' | 'api' | 'prop' | 'd1' | null;
  status: 'loading' | 'ok' | 'warn' | 'error';
  message?: string;
  timestamp?: string;
}

interface SyncTracker {
  nome: FieldSync;
  llm: FieldSync;
  skills: FieldSync;
  tools: FieldSync;
  credenciais: FieldSync;
  canais: FieldSync;
  d1: FieldSync;
}

function nowTimestamp(): string {
  return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function SyncIndicator({ sync }: { sync: FieldSync }) {
  if (!sync || sync.status === 'loading') {
    return <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>;
  }
  const colors: Record<string, string> = { ok: T.success, warn: T.warning, error: T.danger };
  const icons: Record<string, string> = { ok: '\u2713', warn: '\u26A0', error: '\u2717' };
  const sourceLabels: Record<string, string> = { ssh: 'SSH direto', api: 'API fallback', prop: 'Dados iniciais', d1: 'D1 database' };
  return (
    <div style={{ fontSize: 10, color: colors[sync.status], marginTop: 2, opacity: 0.8 }}>
      {icons[sync.status]} {sync.source ? sourceLabels[sync.source] : ''}{sync.message ? ` \u00B7 ${sync.message}` : ''}{sync.timestamp ? ` \u00B7 ${sync.timestamp}` : ''}
    </div>
  );
}

const defaultSyncTracker = (): SyncTracker => ({
  nome: { source: null, status: 'loading' },
  llm: { source: null, status: 'loading' },
  skills: { source: null, status: 'loading' },
  tools: { source: null, status: 'loading' },
  credenciais: { source: null, status: 'loading' },
  canais: { source: null, status: 'loading' },
  d1: { source: null, status: 'loading' },
});

interface Props {
  runnerId: string;
  isOnline: boolean;
  openclawPath?: string;
  cloudMachineId?: string;
  dataDir?: string;
  agentInfo?: {
    name: string;
    emoji?: string;
    status: string;
    defaultProvider?: string;
    defaultModel?: string;
    lastSync?: Date;
  };
  onNameSaved?: (newName: string) => void;
  locked?: boolean;
  editorData?: any;
}

export function AgentGeneral({ runnerId, isOnline, openclawPath, cloudMachineId, dataDir = '/data/.openclaw', editorData, agentInfo, onNameSaved, locked }: Props) {
  const [agentName, setAgentName] = useState('');
  
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [initialName, setInitialName] = useState('');
  
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // LLM config state — direct provider selector
  const [selectedProvider, setSelectedProvider] = useState(''); // '' = auto, 'anthropic', 'openai', 'google', custom-name, '__new__'
  const [selectedModel, setSelectedModel] = useState('');
  const [customToken, setCustomToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [initialSelectedProvider, setInitialSelectedProvider] = useState('');
  const [initialSelectedModel, setInitialSelectedModel] = useState('');
  // Custom providers state
  const [customProviders, setCustomProviders] = useState<Array<{
    name: string; baseUrl: string; apiKey: string; api: string;
    authHeader?: string; customHeaders?: Record<string, string>;
    models: Array<{ id: string; name: string }>;
  }>>([]);
  const [editingProvider, setEditingProvider] = useState<{
    name: string; baseUrl: string; apiKey: string; api: string;
    authHeader?: string; customHeaders?: Record<string, string>;
    models: Array<{ id: string; name: string }>;
  } | null>(null);
  const [fetchingModels, setFetchingModels] = useState(false);
  const [canCustomLLM, setCanCustomLLM] = useState(false);

  // Check if user's plan allows custom LLM providers
  useEffect(() => {
    if (isAdmin.value) { setCanCustomLLM(true); return; }
    api.get<any>('/subscription/me').then(res => {
      try {
        const features = JSON.parse(res?.plan?.features || '{}');
        setCanCustomLLM(!!features.custom_llm_providers);
      } catch { setCanCustomLLM(false); }
    }).catch(() => setCanCustomLLM(false));
  }, []);
  const [autoFetchingModels, setAutoFetchingModels] = useState(false);
  const [initialCustomProviders, setInitialCustomProviders] = useState<string>('[]');
  // Fallback: store raw providers config from openclaw.json for apiKey access
  const rawProvidersRef = useRef<Record<string, any>>({});
  // Router config (used to know available keys)
  const [routerConfig, setRouterConfig] = useState<{
    defaultModel: string;
    agentModel: string;
    availableModels: { id: string; alias: string }[];
    llmKeys: { name: string; masked: string }[];
    providers: { name: string; baseUrl?: string }[];
    envVars: Record<string, string>;
  } | null>(null);
  const [loadedToken, setLoadedToken] = useState(''); // token from config
  const [syncStatus, setSyncStatus] = useState<SyncTracker>(defaultSyncTracker());
  const [showSyncDebug, setShowSyncDebug] = useState(false);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const addLog = (msg: string) => { const ts = new Date().toLocaleTimeString('pt-BR'); setDebugLogs(prev => [...prev, `[${ts}] ${msg}`]); console.log('[AgentGeneral]', msg); };

  const nameChanged = agentName !== initialName;
  

  // ─── Load from editorData (D1, instant) — NO SSH ───
  const loadedFromD1 = useRef(false);
  useEffect(() => {
    // If editorData arrived (from D1), always use it — even if we already loaded from props
    if (editorData?.synced && !loadedFromD1.current) {
      loadedFromD1.current = true;
      loadFromEditorData(editorData);
      return;
    }
    if (editorData && !loadedFromD1.current) {
      loadedFromD1.current = true;
      loadFromEditorData(editorData);
      return;
    }

    // No editorData yet — use agentInfo prop as immediate display
    if (!hasLoadedOnce && agentInfo?.name) {
      applyData(agentInfo.name, '', null);
      setLoading(false);
    } else if (!hasLoadedOnce) {
      setLoading(false);
    }
  }, [runnerId, isOnline, cloudMachineId, editorData]);

  // ─── Load from D1 editor data (instant, no SSH) ───
  function loadFromEditorData(data: any) {
    setLoading(true);
    try {
      const agent = data.agent || {};
      const name = agent.name || agentInfo?.name || 'Agente';
      
      applyData(name, '', null);
      if (onNameSaved && name && name !== 'Agente') onNameSaved(name);

      // LLM config
      const provider = agent.provider || '';
      const model = agent.model || '';
      setSelectedProvider(provider);
      setInitialSelectedProvider(provider);
      setSelectedModel(model);
      setInitialSelectedModel(model);

      // Custom providers from D1
      if (data.custom_providers && data.custom_providers.length > 0) {
        const cps = data.custom_providers.map((cp: any) => ({
          name: cp.name,
          baseUrl: cp.baseUrl || '',
          apiKey: '', // Never sent from backend for security
          api: cp.api || 'openai-completions',
          models: cp.models || [],
        }));
        setCustomProviders(cps);
        setInitialCustomProviders(JSON.stringify(cps));
      }

      // Router config from config_json
      const config = data.config;
      if (config) {
        const defaultModel = config?.agents?.defaults?.model?.primary || '';
        const agentModelFull = (Array.isArray(config?.agents?.list) && config.agents.list[0]?.model) || '';
        const envVarsAll = config?.env?.vars || {};
        const llmKeyNames = ['ANTHROPIC_API_KEY', 'OPENAI_API_KEY', 'GOOGLE_API_KEY', 'GROQ_API_KEY', 'MISTRAL_API_KEY', 'DEEPSEEK_API_KEY'];
        const llmKeys = llmKeyNames
          .filter(k => envVarsAll[k])
          .map(k => ({ name: k, masked: envVarsAll[k].substring(0, 12) + '...' }));
        setRouterConfig({ defaultModel, agentModel: agentModelFull, availableModels: [], llmKeys, providers: [], envVars: envVarsAll });

        // Load existing API key for selected provider
        const providerKeyMap: Record<string, string> = { anthropic: 'ANTHROPIC_API_KEY', openai: 'OPENAI_API_KEY', google: 'GOOGLE_API_KEY' };
        if (providerKeyMap[provider] && envVarsAll[providerKeyMap[provider]]) {
          setCustomToken(envVarsAll[providerKeyMap[provider]]);
          setLoadedToken(envVarsAll[providerKeyMap[provider]]);
        }

        // Store raw providers ref
        if (config?.models?.providers) {
          rawProvidersRef.current = config.models.providers;
        }
      }

      // Sync status — all from D1
      const ts = nowTimestamp();
      const lists = data.lists || {};
      setSyncStatus({
        nome: { source: 'd1', status: 'ok', message: name, timestamp: ts },
        llm: { source: 'd1', status: 'ok', message: provider ? `${provider}/${model}` : '(nao definido)', timestamp: ts },
        skills: { source: 'd1', status: 'ok', message: `${(lists.skills || []).length} habilitadas`, timestamp: ts },
        tools: { source: 'd1', status: 'ok', message: `${(lists.tools || []).length} habilitadas`, timestamp: ts },
        credenciais: { source: 'd1', status: 'ok', message: `${(lists.env_keys || []).length} chaves`, timestamp: ts },
        canais: { source: 'd1', status: 'ok', message: `${(lists.channels || []).length} configurados`, timestamp: ts },
        d1: { source: 'd1', status: 'ok', message: `Sync: ${data.system?.synced_at || 'N/A'}`, timestamp: ts },
      });

      console.log('[AgentGeneral] Loaded from D1 editor data (instant)');
    } catch (e: any) {
      console.error('[AgentGeneral] loadFromEditorData error:', e);
    } finally {
      setLoading(false);
    }
  }

  // loadAgentDetails removed — all loading is via D1 editorData now

  // ─── Auto-fetch models when custom provider is selected ───
  useEffect(() => {
    if (!selectedProvider || ['anthropic', 'openai', 'google', '__new__', ''].includes(selectedProvider)) return;
    if (!hasLoadedOnce) return;
    const cp = customProviders.find(p => p.name === selectedProvider);
    if (!cp) return;
    if (cp.models && cp.models.length > 0) return;
    if (!cp.baseUrl) return;

    let cancelled = false;
    setAutoFetchingModels(true);
    addLog(`Auto-fetching models for custom provider: ${selectedProvider}`);

    (async () => {
      let models: any[] = [];

      // Fetch models via backend API (no SSH)
      try {
        const { api } = await import('../api/client.js');
        const response = await api.get(`/admin/router/custom-models/${selectedProvider}`) as any;
        models = (response?.data?.models || response?.models || []).map((m: any) => typeof m === 'string' ? { id: m, name: m } : { id: m.id, name: m.name || m.id });
        if (models.length > 0) addLog(`Backend: ${models.length} modelos carregados`);
      } catch (e: any) {
        if (!cancelled) addLog(`Fetch models failed: ${e.message}`);
      }

      if (cancelled) return;
      if (models.length > 0) {
        addLog(`Auto-fetched ${models.length} models for ${selectedProvider}`);
        setCustomProviders(prev => prev.map(p =>
          p.name === selectedProvider ? { ...p, models } : p
        ));
        if (!selectedModel) setSelectedModel(models[0].id);
      } else {
        addLog(`No models found for ${selectedProvider}`);
      }
      setAutoFetchingModels(false);
    })();

    return () => { cancelled = true; };
  }, [selectedProvider, hasLoadedOnce]);

  // ─── Sync agent info to D1 (replaces localStorage cache) ───
  async function syncAgentInfoToD1(cfg: any, name: string, desc?: string, skills: string[] = [], tools: string[] = [], integrations: string[] = [], envKeys: string[] = []) {
    const machineId = cloudMachineId || runnerId;
    if (!machineId) return;
    try {
      const { api } = await import('../api/client.js');
      const agent = cfg?.agents?.list?.[0];
      const modelRaw = agent?.model || cfg?.agents?.defaults?.model?.primary || cfg?.agents?.defaults?.model || '';
      const model = typeof modelRaw === 'object' ? (modelRaw.primary || '') : String(modelRaw);
      const parts = model.split('/');
      const channels = cfg?.channels || {};
      const channelKeys = Object.keys(channels).filter(k => {
        const v = channels[k];
        return v && typeof v === 'object' && v.enabled !== false;
      });

      const payload: Record<string, any> = {
        agent_name: name || '',
        agent_model: parts.length > 1 ? parts.slice(1).join('/') : model,
        agent_provider: parts.length > 1 ? parts[0] : '',
        agent_language: '',
        agent_brain: cfg?.brain || '',
        channels: JSON.stringify(channelKeys),
        oc_path: openclawPath || '',
        is_docker: isDockerMode(openclawPath),
        node_version: '',
      };
      // Skills: only sent from OpenClawSkills tab (source of truth for active skills)
      // Don't overwrite skills from here — pass empty array = skip
      if (skills.length > 0) payload.skills = JSON.stringify(skills);
      // Always send tools + integrations + env_keys
      payload.tools = JSON.stringify(tools);
      payload.integrations = JSON.stringify(integrations);
      if (envKeys.length > 0) payload.env_keys = JSON.stringify(envKeys);
      console.log('[AgentDetails] D1 PUT payload:', { machineId, model, provider: payload.agent_provider, skills: skills.length, tools: tools.length, integrations: integrations.length, envKeys: envKeys.length, channels: channelKeys.length });
      const result = await api.put(`/cloud/machines/${machineId}/agent-info`, payload);
 console.log('[AgentDetails] D1 PUT result:', result);
      setSyncStatus(prev => ({ ...prev, d1: { source: 'd1', status: 'ok', message: 'Salvo no D1', timestamp: nowTimestamp() } }));
    } catch (e: any) {
      console.warn('[AgentDetails] Failed to sync to D1:', e);
      setSyncStatus(prev => ({ ...prev, d1: { source: 'd1', status: 'error', message: e?.message || 'Falha D1', timestamp: nowTimestamp() } }));
    }
  }

  function applyData(name: string, _desc: string, created: string | null | undefined) {
    setAgentName(name);
    setInitialName(name);
    setHasLoadedOnce(true);
    if (created) setCreatedAt(created);
    console.log('[AgentDetails] applyData:', { name, created });
  }

  // ─── Save: PATCH validates, then write config via heredoc (no jq dependency) ───
  async function handleSave() {
    if (!hasChangesAll || !cloudMachineId) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const { api } = await import('../api/client.js');

      // PATCH endpoint validates
      try {
        await api.patch(`/cloud/machines/${cloudMachineId}/update-agent-details`, {
          agentName,
        });
      } catch (e) {
        console.warn('[AgentDetails] PATCH validation skipped:', e);
      }

 // 2⃣ Build merge payload for config_patch (no SSH write — queue-based)
      const merge: any = {};

      // Agent name
      merge.agents = {
        list: [{ name: agentName, identity: { name: agentName } }],
      };

      // Validate models integrity before saving
      for (const cp of customProviders) {
        if (!Array.isArray(cp.models)) {
 console.warn(`[AgentDetails] ${cp.name} models não é array, corrigindo...`);
          (cp as any).models = [];
        }
      }

      // Custom providers
      if (customProvidersChanged) {
        const newProviders: Record<string, any> = {};
        for (const cp of customProviders) {
          const existingApiKey = rawProvidersRef.current?.[cp.name]?.apiKey || '';
          newProviders[cp.name] = {
            baseUrl: cp.baseUrl,
            apiKey: cp.apiKey || existingApiKey,
            api: cp.api,
            ...(cp.authHeader && cp.authHeader !== 'Authorization' ? { authHeader: cp.authHeader } : {}),
            ...(cp.customHeaders && Object.keys(cp.customHeaders).length > 0 ? { headers: cp.customHeaders } : {}),
            models: cp.models || [],
          };
        }
        merge.models = { providers: newProviders };
      }

      // Model selection
      if (selectedProvider && selectedProvider !== '__new__' && selectedModel) {
        const fullModel = `${selectedProvider}/${selectedModel}`;
        if (!merge.agents) merge.agents = {};
        if (!merge.agents.list) merge.agents.list = [{}];
        merge.agents.list[0].model = fullModel;
        merge.agents.defaults = { model: { primary: fullModel } };

        // API key for built-in providers → env.vars
        const providerKeyMap: Record<string, string> = { anthropic: 'ANTHROPIC_API_KEY', openai: 'OPENAI_API_KEY', google: 'GOOGLE_API_KEY' };
        if (providerKeyMap[selectedProvider] && customToken && customToken !== loadedToken) {
          merge.env = { vars: { [providerKeyMap[selectedProvider]]: customToken } };
        }
        // API key for custom providers → models.providers
        if (!providerKeyMap[selectedProvider] && customToken && customToken !== loadedToken) {
          if (!merge.models) merge.models = {};
          if (!merge.models.providers) merge.models.providers = {};
          merge.models.providers[selectedProvider] = { apiKey: customToken };
        }
      }

 // 3⃣ Enqueue config_patch (runner does deepMerge + write + restart)
      console.log('[AgentDetails] Enqueuing config_patch:', Object.keys(merge));
      const queueResult = await api.post(`/cloud/machines/${cloudMachineId}/queue-action`, {
        action: 'config_patch',
        payload: { merge, restart: true },
        source: 'agent_general',
      }) as any;
      const queueData = queueResult?.data || queueResult;
      console.log('[AgentDetails] Queue result:', queueData);

      // Provision provider key in D1 (non-fatal)
      if (hasLlmChanges && customToken && customToken !== loadedToken && selectedProvider && selectedProvider !== '__new__') {
        try {
          await api.post(`/cloud/machines/${cloudMachineId}/configure-openclaw`, {
            selectedProvider,
            openclawPath,
            providerKeys: { [selectedProvider]: customToken },
            skipConfigWrite: true,
          });
        } catch (e) {
          console.warn('[AgentDetails] configure-openclaw key provisioning failed (non-fatal):', e);
        }
      }

 // Success — notify parent immediately with new name
 console.log('[AgentDetails] SAVE OK:', { agentName });
      setInitialName(agentName);
      setInitialSelectedProvider(selectedProvider);
      setInitialSelectedModel(selectedModel);
      setInitialCustomProviders(JSON.stringify(customProviders));
      if (customToken && customToken !== loadedToken) setLoadedToken(customToken);
      if (onNameSaved) onNameSaved(agentName);
      
      // Sync to D1
      syncAgentInfoToD1(merge, agentName);
      if (hasLlmChanges && selectedProvider && selectedProvider !== '__new__') {
        api.put(`/cloud/machines/${cloudMachineId}/agent-info`, {
          agent_provider: selectedProvider,
          agent_model: selectedModel,
        }).catch(console.warn);
      }

      const immediate = queueData?.immediate || queueData?.status === 'done';
 setSaveResult({ ok: true, msg: immediate ? 'Salvo e aplicado!' : 'Salvo! Aplicando configuração...' });
      setTimeout(() => setSaveResult(null), 4000);
    } catch (err: any) {
      console.error('[AgentDetails] Save error:', err.message);
      setSaveResult({ ok: false, msg: `Erro ao salvar: ${err?.message || 'Desconhecido'}` });
      setTimeout(() => setSaveResult(null), 5000);
    } finally {
      setSaving(false);
    }
  }

  
  
  
  const customProvidersChanged = JSON.stringify(customProviders) !== initialCustomProviders;
  const hasLlmChanges = selectedProvider !== initialSelectedProvider ||
    selectedModel !== initialSelectedModel ||
    customProvidersChanged ||
    (customToken !== loadedToken && customToken !== '');
  const hasChangesAll = nameChanged || hasLlmChanges;

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>
        <div style={{
          width: 24, height: 24, margin: '0 auto 14px',
          border: `2px solid ${T.whiteAlpha8}`, borderTopColor: T.accent,
          borderRadius: '50%', animation: 'spin 0.8s linear infinite',
        }} />
        <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
        <div style={{ fontSize: 11, marginTop: 6, color: T.textDisabled }}>Lendo dados do agente</div>
      </div>
    );
  }

  const MODEL_OPTIONS: Record<string, Array<{value: string; label: string}>> = {
    anthropic: [
      { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5' },
      { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
      { value: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
    ],
    openai: [
      { value: 'gpt-4o', label: 'GPT-4o' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
      { value: 'o3-mini', label: 'o3-mini' },
    ],
    google: [
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    ],
  };

  const TOKEN_PLACEHOLDERS: Record<string, string> = {
    anthropic: 'sk-ant-api03-...',
    openai: 'sk-...',
    google: 'AIza...',
  };

  const maskToken = (t: string) => t ? t.slice(0, 8) + '••••••••' : '';

  // Languages removed — OpenClaw has no native language field

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    background: 'var(--bg)', border: '1px solid var(--border)',
    color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const,
    fontWeight: 600, cursor: 'text' as const, pointerEvents: 'auto' as const,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'relative' }}>
      <LockOverlay locked={locked} />
      {/* Nome do Agente */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 4 }}>
          Nome do Agente <span style={{ color: T.danger }}>*</span>
        </label>
        <input
          type="text"
          value={agentName}
          onInput={(e: any) => setAgentName(e.target.value.slice(0, 50))}
          onChange={(e: any) => setAgentName(e.target.value.slice(0, 50))}
          placeholder="ex: Marketing Bot, Assistente, etc"
          maxLength={50}
          style={{ ...inputStyle, border: nameChanged ? `2px solid ${T.accentBgStrong}` : '1px solid var(--border)' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
          {!agentName.trim() ? (
            <span style={{ fontSize: 10, color: T.danger }}>Nome é obrigatório</span>
          ) : <span />}
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{agentName.length}/50</span>
        </div>
        <SyncIndicator sync={syncStatus.nome} />
      </div>

      {/* Inteligencia LLM — Direct Provider Selector */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 4 }}>
          Inteligencia LLM
        </label>
        <select
          value={selectedProvider}
          onChange={(e: any) => {
            const v = e.target.value;
            setSelectedProvider(v);
            if (v === '__new__') {
              setEditingProvider({ name: '', baseUrl: '', apiKey: '', api: 'openai-completions', models: [] });
              setSelectedModel('');
            } else if (['anthropic', 'openai', 'google'].includes(v)) {
              setSelectedModel(MODEL_OPTIONS[v]?.[0]?.value || '');
              setCustomToken('');
              setLoadedToken('');
              // Load existing token if available
              const keyMap: Record<string, string> = { anthropic: 'ANTHROPIC_API_KEY', openai: 'OPENAI_API_KEY', google: 'GOOGLE_API_KEY' };
              const existingKey = (keyMap[v] && routerConfig?.envVars?.[keyMap[v]]) || '';
              if (existingKey) { setCustomToken(existingKey); setLoadedToken(existingKey); }
            } else {
              // Custom provider selected
              const cp = customProviders.find(p => p.name === v);
              const rawP = rawProvidersRef.current?.[v];
              // Restore original model if switching back to initial provider
              if (v === initialSelectedProvider && initialSelectedModel) {
                setSelectedModel(initialSelectedModel);
              } else {
                const firstModel = cp?.models?.[0]?.id
                  || (rawP?.models?.[0]?.id)
                  || '';
                setSelectedModel(firstModel);
              }
              // Restore apiKey for custom provider
              const cpKey = cp?.apiKey || rawP?.apiKey || '';
              if (cpKey) { setCustomToken(cpKey); setLoadedToken(cpKey); }
              else { setCustomToken(''); setLoadedToken(''); }
              setEditingProvider(null);
            }
          }}
          style={{
            ...inputStyle, fontWeight: 500, cursor: 'pointer',
            appearance: 'auto' as any, WebkitAppearance: 'auto' as any,
            border: (selectedProvider !== initialSelectedProvider) ? `2px solid ${T.accentBgStrong}` : '1px solid var(--border)',
          }}
        >
          <option value="" disabled>Selecionar provedor...</option>
          {/* Custom providers FIRST (Elon Router appears on top) */}
          {(() => {
            // Merge customProviders + initialProvider (never lose the current one)
            const cpNames = customProviders.map(cp => cp.name);
            const allCustom = [...customProviders];
            // If initial provider was custom and not in list, add fallback entry
            if (initialSelectedProvider && !['anthropic', 'openai', 'google', '__new__', ''].includes(initialSelectedProvider) && !cpNames.includes(initialSelectedProvider)) {
              allCustom.unshift({ name: initialSelectedProvider, baseUrl: '', apiKey: '', api: 'openai-completions', models: [] });
            }
            // Sort: 'elon' first
            allCustom.sort((a, b) => a.name === 'elon' ? -1 : b.name === 'elon' ? 1 : 0);
            return allCustom
              .filter(cp => cp.name === 'elon' || canCustomLLM)
              .map(cp => (
              <option key={cp.name} value={cp.name}>
                {cp.name === 'elon' ? 'Elon Router' : cp.name}
              </option>
            ));
          })()}
          <option value="anthropic">Anthropic (Claude)</option>
          <option value="openai">OpenAI (GPT)</option>
          <option value="google">Google (Gemini)</option>
          {canCustomLLM && <option value="__new__">+ Provedor Customizado</option>}
        </select>
        <SyncIndicator sync={syncStatus.llm} />
      </div>

      {/* Built-in provider config (Anthropic/OpenAI/Google) */}
      {['anthropic', 'openai', 'google'].includes(selectedProvider) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', background: T.bgSubtle, borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div>
            <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Modelo</label>
            <select
              value={selectedModel}
              onChange={(e: any) => setSelectedModel(e.target.value)}
              style={{ ...inputStyle, fontWeight: 500, cursor: 'pointer', appearance: 'auto' as any, WebkitAppearance: 'auto' as any }}
            >
              {(MODEL_OPTIONS[selectedProvider] || []).map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>API Key</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showToken ? 'text' : 'password'}
                value={customToken === loadedToken && loadedToken ? maskToken(loadedToken) : customToken}
                onFocus={() => { if (customToken === loadedToken && loadedToken) setCustomToken(''); }}
                onInput={(e: any) => setCustomToken(e.target.value)}
                placeholder={TOKEN_PLACEHOLDERS[selectedProvider] || 'API key...'}
                style={{ ...inputStyle, fontWeight: 400, paddingRight: 60 }}
              />
              <Button variant="ghost" type="button" onClick={() => setShowToken(!showToken)}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11 }}>
                {showToken ? 'Ocultar' : 'Mostrar'}
              </Button>
            </div>
            <div style={{ fontSize: 9, color: T.textDisabled, marginTop: 3 }}>
              Fonte: openclaw.json → env.vars.{({ anthropic: 'ANTHROPIC_API_KEY', openai: 'OPENAI_API_KEY', google: 'GOOGLE_API_KEY' } as Record<string,string>)[selectedProvider]}
            </div>
          </div>
        </div>
      )}

      {/* Custom provider selected — SAME layout as built-in: Modelo + API Key + Copy */}
      {selectedProvider && !['anthropic', 'openai', 'google', '__new__', ''].includes(selectedProvider) && (() => {
        const cp = customProviders.find(p => p.name === selectedProvider);
        // Fallback: read from raw config ref if customProviders hasn't loaded yet
        const rawProvider = rawProvidersRef.current?.[selectedProvider];
        // Build models list: from customProviders if loaded, raw config, or fallback with current model
        const modelsList = cp?.models && cp.models.length > 0
          ? cp.models
          : rawProvider?.models && Array.isArray(rawProvider.models) && rawProvider.models.length > 0
            ? rawProvider.models.map((m: any) => ({ id: m.id || '', name: m.name || m.id || '' }))
            : selectedModel ? [{ id: selectedModel, name: selectedModel }] : [];
        const isElon = selectedProvider === 'elon' || cp?.baseUrl?.includes(window.location.host) || rawProvider?.baseUrl?.includes(window.location.host);
        const apiKey = cp?.apiKey || rawProvider?.apiKey || '';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', background: T.bgSubtle, borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
            {/* Modelo dropdown — same as built-in */}
            <div>
              <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Modelo</label>
              {autoFetchingModels ? (
                <div style={{ padding: '8px 0' }}><SkeletonBar width="50%" height={11} /></div>
              ) : modelsList.length > 0 ? (
                <select value={selectedModel} onChange={(e: any) => setSelectedModel(e.target.value)}
                  style={{ ...inputStyle, fontWeight: 500, cursor: 'pointer', appearance: 'auto' as any, WebkitAppearance: 'auto' as any }}>
                  {isElon && <option value="auto">Autom&#225;tico</option>}
                  {modelsList.filter(m => m.id !== 'auto').map(m => <option key={m.id} value={m.id}>{m.name || m.id}</option>)}
                </select>
              ) : (
                <div style={{ fontSize: 11, color: T.textMuted, padding: '8px 0' }}>{'\u26A0\uFE0F'} Nenhum modelo encontrado</div>
              )}
            </div>

            {/* API Key — editable, same pattern as built-in providers */}
            <div>
              <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>API Key</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showToken ? 'text' : 'password'}
                  value={customToken || ''}
                  onFocus={() => { if (!customToken && apiKey) setCustomToken(apiKey); }}
                  onInput={(e: any) => {
                    setCustomToken(e.target.value);
                    // Also update customProviders state so save picks it up
                    if (cp) {
                      setCustomProviders(prev => prev.map(p =>
                        p.name === selectedProvider ? { ...p, apiKey: e.target.value } : p
                      ));
                    }
                  }}
                  placeholder={isElon ? 'ert_live_...' : 'API key...'}
                  style={{ ...inputStyle, fontWeight: 400, paddingRight: apiKey || customToken ? 110 : 10, color: T.textSecondary }}
                />
                <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 4 }}>
                  {(apiKey || customToken) && (
                    <Button variant="ghost" type="button" onClick={() => {
                      const keyToCopy = customToken || apiKey;
                      navigator.clipboard.writeText(keyToCopy).then(() => {
                        const btn = document.querySelector('[data-copy-key-custom]') as HTMLElement;
                        if (btn) { btn.textContent = '\u2705'; setTimeout(() => { btn.textContent = '\uD83D\uDCCB'; }, 2000); }
                      });
                    }}
                      data-copy-key-custom
                      style={{ background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`, borderRadius: 4, color: T.primary, cursor: 'pointer', fontSize: 10, fontWeight: 600, padding: '3px 6px' }}>
                      {'\uD83D\uDCCB'}
                    </Button>
                  )}
                  <Button variant="ghost" type="button" onClick={() => setShowToken(!showToken)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 10 }}>
                    {showToken ? 'Ocultar' : 'Mostrar'}
                  </Button>
                </div>
              </div>
              {isElon && !apiKey && !customToken && (
                <div style={{ fontSize: 10, color: T.warning, marginTop: 4 }}>
                  {'\u26A0\uFE0F'} Chave não encontrada. Clique em "Editar" para regenerar ou cole sua chave Elon Router acima.
                </div>
              )}
              <div style={{ fontSize: 9, color: T.textDisabled, marginTop: 3 }}>
                Fonte: openclaw.json {'\u2192'} models.providers.{selectedProvider}.apiKey
              </div>
            </div>

            {/* Edit/Delete buttons */}
            <div style={{ display: 'flex', gap: 6 }}>
              <Button variant="ghost" onClick={() => { if (cp) { setEditingProvider({ ...cp }); setSelectedProvider('__new__'); } }}
                style={{ background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`, borderRadius: 6, color: T.primary, fontSize: 10, fontWeight: 600, padding: '4px 12px', cursor: 'pointer' }}>
                Editar</Button>
              <Button variant="ghost" onClick={() => {
                setCustomProviders(prev => prev.filter(p => p.name !== selectedProvider));
                setSelectedProvider(''); setSelectedModel('');
              }} style={{ background: T.dangerBg, border: `1px solid ${T.dangerBgStrong}`, borderRadius: 6, color: T.danger, fontSize: 10, fontWeight: 600, padding: '4px 12px', cursor: 'pointer' }}>
                Excluir</Button>
            </div>
          </div>
        );
      })()}

      {/* New/Edit custom provider form */}
      {selectedProvider === '__new__' && editingProvider && (
        <div style={{ padding: '12px 14px', background: T.bgCard, borderRadius: 8, border: `1px solid ${T.whiteAlpha8}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary }}>
              {customProviders.some(p => p.name === editingProvider.name) ? 'Editar Provedor' : 'Novo Provedor'}
            </div>
            {/* Usar LLM ELON — preenche tudo com 1 click */}
            {!customProviders.some(p => p.name === editingProvider.name) && (
              <Button variant="ghost"
                onClick={async () => {
                  try {
                    addLog('Configurando Elon Router...');
                    const { api: apiClient } = await import('../api/client.js');
                    // Buscar token existente do customer, ou criar novo
                    let routerToken = '';
                    try {
                      const tokensRes = await apiClient.get<{ tokens: Array<{ id: string; token_prefix: string; plaintext_token?: string; name: string }> }>('/router/tokens');
                      const tokens = tokensRes?.tokens || (tokensRes as any)?.data?.tokens || [];
                      if (tokens.length > 0) {
                        // Usar o primeiro token existente (prefix only — user já tem)
                        routerToken = tokens[0].token_prefix + '...';
                        addLog(`Token existente encontrado: ${tokens[0].name || tokens[0].token_prefix}`);
                      }
                    } catch (e: any) {
                      addLog(`Aviso: não foi possível buscar tokens (${e.message})`);
                    }
                    // Se não tem token, criar um novo
                    if (!routerToken || routerToken.endsWith('...')) {
                      try {
                        const createRes = await apiClient.post<{ token: any; plaintext_token: string }>('/router/tokens', {
                          name: 'Elon Router (auto)',
                        });
                        const data = (createRes as any)?.data || createRes;
                        if (data?.plaintext_token) {
                          routerToken = data.plaintext_token;
                          addLog(`Novo token criado: ${routerToken.substring(0, 12)}...`);
                        }
                      } catch (e: any) {
                        addLog(`Aviso: não foi possível criar token (${e.message}). Preencha manualmente.`);
                      }
                    }
                    // Fetch models via backend API (no SSH)
                    let elonModels: Array<{ id: string; name: string }> = [];
                    try {
                      const response = await apiClient.get('/admin/router/custom-models/elon') as any;
                      const fetchedModels = (response?.data?.models || response?.models || []).map((m: any) => typeof m === 'string' ? { id: m, name: m } : { id: m.id, name: m.name || m.id });
                      if (fetchedModels.length > 0) {
                        elonModels = fetchedModels;
                        addLog(`Carregados ${elonModels.length} modelos via API`);
                      }
                    } catch (e: any) {
                      addLog(`Fallback: nao foi possivel buscar modelos (${e.message})`);
                    }
                    // Fallback para hardcoded se fetch falhou
                    if (elonModels.length === 0) {
                      elonModels = [
                        { id: 'elon/claude-haiku-4-5', name: 'Claude Haiku 4.5' },
                        { id: 'elon/claude-sonnet-4-5', name: 'Claude Sonnet 4.5' },
                        { id: 'elon/claude-opus-4-6', name: 'Claude Opus 4.6' },
                        { id: 'elon/gpt-4o', name: 'GPT-4o' },
                        { id: 'elon/gpt-4o-mini', name: 'GPT-4o Mini' },
                      ];
                      addLog(`Usando ${elonModels.length} modelos hardcoded como fallback`);
                    }
                    // Preencher todos os campos do Elon Router
                    setEditingProvider({
                      name: 'elon',
                      baseUrl: `${window.location.origin}/api/v1/llm/v1`,
                      apiKey: routerToken,
                      api: 'openai-completions',
                      authHeader: '',
                      customHeaders: {},
                      models: elonModels,
                    });
                    addLog('Elon Router configurado! Clique em Salvar Provedor.');
                  } catch (e: any) {
                    addLog(`Erro ao configurar Elon Router: ${e.message}`);
                  }
                }}
                style={{
                  background: `linear-gradient(135deg, ${T.accentBg}, ${T.purpleBg})`,
                  border: `1px solid ${T.accentBgStrong}`,
                  borderRadius: 6, color: T.purple, fontSize: 10, fontWeight: 700,
                  padding: '5px 14px', cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                Usar LLM ELON
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div>
              <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Nome (ID)</label>
              <input value={editingProvider.name}
                onInput={(e: any) => setEditingProvider({ ...editingProvider, name: e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase() })}
                placeholder="ex: openrouter, ollama-local"
                disabled={customProviders.some(p => p.name === editingProvider.name)}
                style={{ ...inputStyle, fontSize: 12, padding: '6px 8px', fontWeight: 400 }} />
            </div>
            <div>
              <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Base URL *</label>
              <input value={editingProvider.baseUrl}
                onInput={(e: any) => setEditingProvider({ ...editingProvider, baseUrl: e.target.value })}
                placeholder="https://api.example.com/v1"
                style={{ ...inputStyle, fontSize: 12, padding: '6px 8px', fontWeight: 400 }} />
            </div>
            <div>
              <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>API Key (opcional)</label>
              <input type="password" value={editingProvider.apiKey}
                onInput={(e: any) => setEditingProvider({ ...editingProvider, apiKey: e.target.value })}
                placeholder="sk-..."
                style={{ ...inputStyle, fontSize: 12, padding: '6px 8px', fontWeight: 400 }} />
            </div>
            <div>
              <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Protocolo API</label>
              <select value={editingProvider.api}
                onChange={(e: any) => setEditingProvider({ ...editingProvider, api: e.target.value })}
                style={{ ...inputStyle, fontSize: 12, padding: '6px 8px', cursor: 'pointer', appearance: 'auto' as any, WebkitAppearance: 'auto' as any }}>
                <option value="openai-completions">openai-completions</option>
                <option value="anthropic-messages">anthropic-messages</option>
                <option value="openai-responses">openai-responses</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Auth Header (padrao: Authorization)</label>
              <input value={editingProvider.authHeader || ''}
                onInput={(e: any) => setEditingProvider({ ...editingProvider, authHeader: e.target.value })}
                placeholder="Authorization"
                style={{ ...inputStyle, fontSize: 12, padding: '6px 8px', fontWeight: 400 }} />
            </div>
            <div>
              <label style={{ fontSize: 10, color: T.textMuted, display: 'block', marginBottom: 2 }}>Headers Customizados (chave: valor, um por linha)</label>
              <Textarea
                value={Object.entries(editingProvider.customHeaders || {}).map(([k, v]) => `${k}: ${v}`).join('\n')}
                onInput={(e: any) => {
                  const headers: Record<string, string> = {};
                  (e.target.value || '').split('\n').forEach((line: string) => {
                    const idx = line.indexOf(':');
                    if (idx > 0) headers[line.substring(0, idx).trim()] = line.substring(idx + 1).trim();
                  });
                  setEditingProvider({ ...editingProvider, customHeaders: headers });
                }}
                placeholder="X-Custom-Header: valor"
                rows={2}
                style={{ ...inputStyle, fontSize: 11, padding: '6px 8px', fontWeight: 400, resize: 'vertical' as const, fontFamily: 'monospace' }} />
            </div>

            {/* Models */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontSize: 10, color: T.textMuted }}>Modelos ({editingProvider.models.length})</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button variant="ghost" onClick={async () => {
                    if (!editingProvider.baseUrl) return;
                    setFetchingModels(true);
                    try {
                      const { api } = await import('../api/client.js');
                      // Use backend proxy to fetch models (no SSH)
                      const providerName = editingProvider.name || 'custom';
                      const response = await api.get(`/admin/router/custom-models/${providerName}`) as any;
                      const models = (response?.data?.models || response?.models || []).map((m: any) => typeof m === 'string' ? { id: m, name: m } : { id: m.id, name: m.name || m.id });
                      if (models.length > 0) {
                        setEditingProvider({ ...editingProvider, models });
                        addLog(`Fetched ${models.length} models from API`);
                      } else {
                        addLog('Nenhum modelo encontrado na resposta');
                      }
                    } catch (e: any) {
                      addLog(`Erro ao buscar modelos: ${e.message}`);
                    } finally {
                      setFetchingModels(false);
                    }
                  }} disabled={fetchingModels || !editingProvider.baseUrl}
                    style={{ background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`, borderRadius: 4, color: T.primary, fontSize: 9, fontWeight: 600, padding: '2px 8px', cursor: 'pointer', opacity: fetchingModels ? 0.5 : 1 }}>
                    {fetchingModels ? 'Buscando...' : 'Buscar Modelos'}
                  </Button>
                  <Button variant="ghost" onClick={() => setEditingProvider({ ...editingProvider, models: [...editingProvider.models, { id: '', name: '' }] })}
                    style={{ background: T.bgCardHover, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: T.textSecondary, fontSize: 9, fontWeight: 600, padding: '2px 8px', cursor: 'pointer' }}>
                    + Manual
                  </Button>
                </div>
              </div>
              {editingProvider.models.map((m, mi) => (
                <div key={mi} style={{ display: 'flex', gap: 4, marginBottom: 3 }}>
                  <input value={m.id} placeholder="model-id"
                    onInput={(e: any) => { const ms = [...editingProvider!.models]; ms[mi] = { id: e.target.value, name: ms[mi]?.name || '' }; setEditingProvider({ ...editingProvider!, models: ms }); }}
                    style={{ ...inputStyle, fontSize: 11, padding: '4px 6px', fontWeight: 400, flex: 1 }} />
                  <input value={m.name} placeholder="Nome exibicao"
                    onInput={(e: any) => { const ms = [...editingProvider!.models]; ms[mi] = { id: ms[mi]?.id || '', name: e.target.value }; setEditingProvider({ ...editingProvider!, models: ms }); }}
                    style={{ ...inputStyle, fontSize: 11, padding: '4px 6px', fontWeight: 400, flex: 1 }} />
                  <Button variant="ghost" onClick={() => { const ms = editingProvider.models.filter((_, i) => i !== mi); setEditingProvider({ ...editingProvider, models: ms }); }}
                    style={{ background: 'none', border: 'none', color: T.danger, cursor: 'pointer', fontSize: 11, padding: '0 4px' }}>x</Button>
                </div>
              ))}
            </div>

            {/* Form actions */}
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <Button variant="ghost" onClick={() => {
                if (!editingProvider.name || !editingProvider.baseUrl) return;
                const existing = customProviders.findIndex(p => p.name === editingProvider.name);
                if (existing >= 0) {
                  setCustomProviders(prev => prev.map((p, i) => i === existing ? editingProvider : p));
                } else {
                  setCustomProviders(prev => [...prev, editingProvider]);
                }
                // Auto-select the new/edited provider
                setSelectedProvider(editingProvider.name);
                setSelectedModel(editingProvider.models[0]?.id || '');
                setEditingProvider(null);
              }} disabled={!editingProvider.name || !editingProvider.baseUrl}
                style={{
                  flex: 1, padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  background: T.successBg, border: `1px solid ${T.successBgStrong}`, color: T.success,
                  opacity: (!editingProvider.name || !editingProvider.baseUrl) ? 0.4 : 1,
                }}>Salvar Provedor</Button>
              <Button variant="ghost" onClick={() => { setSelectedProvider(initialSelectedProvider); setSelectedModel(initialSelectedModel); setEditingProvider(null); }}
                style={{ padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: T.bgCardHover, border: '1px solid rgba(255,255,255,0.1)', color: T.textSecondary }}>
                Cancelar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Active model summary */}
      {selectedProvider && selectedProvider !== '__new__' && selectedModel && (
        <div style={{ fontSize: 10, color: T.textMuted, padding: '6px 10px', background: T.accentBg, borderRadius: 6, border: `1px solid ${T.accentBg}` }}>
          Modelo ativo: <span style={{ color: T.textSecondary, fontWeight: 600, fontFamily: 'monospace' }}>
            {selectedProvider}/{selectedModel}
          </span>
        </div>
      )}

      {/* Footer: Criado em (discreto) */}
      {createdAt && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 4 }}>
          Criado em {new Date(createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
        </div>
      )}

      {/* Save footer — sticky at bottom */}
      <div style={{
        position: 'sticky', bottom: -20, marginLeft: -24, marginRight: -24,
        padding: '12px 24px', background: T.bg,
        borderTop: `1px solid ${T.whiteAlpha8}`,
        display: 'flex', flexDirection: 'column', gap: 8,
        zIndex: 10,
      }}>
        {saveResult && (
          <div style={{
            padding: '8px 12px', borderRadius: 8,
            background: saveResult.ok ? T.successBg : T.dangerBg,
            fontSize: 12, fontWeight: 600, color: saveResult.ok ? T.success : T.danger,
          }}>
            {saveResult.msg}
          </div>
        )}
        <Button variant="ghost"
          onClick={handleSave}
          disabled={!hasChangesAll || saving || !agentName.trim()}
          style={{
            width: '100%', padding: '12px 16px', borderRadius: 10,
            background: hasChangesAll ? T.successBg : T.bgCard,
            border: hasChangesAll ? `1px solid ${T.successBgStrong}` : `1px solid ${T.whiteAlpha8}`,
            color: hasChangesAll ? T.success : T.textDisabled,
            fontSize: 13, fontWeight: 700,
            cursor: hasChangesAll && !saving ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: saving ? 0.6 : 1,
            transition: 'all 0.2s',
          }}
        >
          {saving ? 'Salvando...' : hasChangesAll ? 'Salvar Detalhes' : 'Nenhuma alteração'}
        </Button>
      </div>

      {/* Technical panels — admin only */}
      {isAdmin.value && (<>
        {/* Connection Info */}
        <div style={{ marginTop: 8, padding: '8px 10px', background: T.primaryBg, borderRadius: 8, fontSize: 10, color: T.textMuted, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div><strong style={{ color: T.textSecondary }}>Runner:</strong> {runnerId}</div>
          <div><strong style={{ color: T.textSecondary }}>openclawPath:</strong> {openclawPath || '(não definido)'}</div>
          <div><strong style={{ color: T.textSecondary }}>Docker:</strong> {isDockerMode(openclawPath) ? `Sim (container: ${openclawPath?.replace('docker:', '')})` : 'Não'}</div>
          <div><strong style={{ color: T.textSecondary }}>dataDir:</strong> {dataDir}</div>
          <div><strong style={{ color: T.textSecondary }}>cloudMachineId:</strong> {cloudMachineId || '(não definido)'}</div>
        </div>

        {/* Sync Debug */}
        <div style={{ marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
          <div onClick={() => setShowSyncDebug(!showSyncDebug)} style={{ fontSize: 10, color: T.textMuted, cursor: 'pointer', userSelect: 'none' }}>
            {showSyncDebug ? '\u25BC' : '\u25B6'} Sync Debug
          </div>
          {showSyncDebug && (
            <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {([
                { key: 'nome' as const, label: 'Nome', path: 'openclaw.json → agents.list[].name' },
                { key: 'llm' as const, label: 'LLM', path: 'openclaw.json → agents.defaults.model' },
                { key: 'skills' as const, label: 'Skills', path: 'API /available-skills + /agents/skills' },
                { key: 'tools' as const, label: 'Tools', path: 'openclaw.json → tools.allow/deny' },
                { key: 'credenciais' as const, label: 'Credenciais', path: 'workspace/.env' },
                { key: 'canais' as const, label: 'Canais', path: 'openclaw.json → channels' },
                { key: 'd1' as const, label: 'D1 Database', path: 'machine_agent_info' },
              ]).map(({ key, label, path }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  <span style={{ fontSize: 10, color: T.textSecondary, width: 70, fontWeight: 600, flexShrink: 0 }}>{label}</span>
                  <div style={{ flex: 1 }}>
                    <SyncIndicator sync={syncStatus[key]} />
                    <div style={{ fontSize: 9, color: T.textDisabled, marginTop: 1 }}>{path}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Debug Logs Panel */}
        {debugLogs.length > 0 && (
          <div style={{ marginTop: 12, borderTop: `1px solid ${T.whiteAlpha8}`, paddingTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.warning, marginBottom: 6 }}>Debug Logs ({debugLogs.length})</div>
            <div style={{
              background: T.bg, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
              padding: 10, maxHeight: 300, overflowY: 'auto', fontFamily: 'monospace', fontSize: 10,
              lineHeight: 1.6, color: T.textSecondary,
            }}>
              {debugLogs.map((log, i) => (
 <div key={i} style={{ color: log.includes('FAILED') || log.includes('Error') ? T.danger : log.includes('[OK]') || log.includes('success') ? T.success : T.textSecondary }}>
                  {log}
                </div>
              ))}
            </div>
          </div>
        )}
      </>)}
    </div>
  );
}
